import * as React from "react";
import { Typography, Skeleton, Box } from "@mui/material";
import NavigationIcon from "@mui/icons-material/Navigation";
import { WindData } from "../../services/WeatherService";
import { formatWindInfo } from "../../utils/wind";

export interface WindDisplayProps {
  loading: boolean;
  windData: WindData | null;
  error: string | null;
  useMetric: boolean;
  compact?: boolean;
}

/**
 * WindDisplay - Displays wind speed and direction (presentational component)
 * Parent component should fetch wind data and pass it as props
 */
export const WindDisplay: React.FC<WindDisplayProps> = ({
  loading,
  windData,
  error,
  useMetric,
  compact = false,
}) => {
  // Format wind information
  const windText = React.useMemo(() => {
    if (loading) {
      return "Loading...";
    }
    if (error || !windData) {
      return "--";
    }
    const formatted = formatWindInfo(windData.speedMps, windData.directionDegrees, useMetric);
    return formatted;
  }, [loading, error, windData, useMetric]);

  // Calculate rotation angle: wind blows TO (directionDegrees + 180)
  const rotationAngle = windData?.directionDegrees !== undefined
    ? (windData.directionDegrees + 180) % 360
    : 0;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
    >
      {/* Show arrow only when we have valid wind data */}
      {!loading && !error && windData && (
        <NavigationIcon
          sx={{
            fontSize: 14,
            color: "info.dark",
            transform: `rotate(${rotationAngle}deg)`,
            transition: "transform 0.3s ease",
          }}
        />
      )}
      <Typography
        variant="caption"
        textAlign="center"
        color="info.dark"
      >
        {loading ? (
          <Skeleton
            variant="text"
            width={compact ? 60 : 80}
            height={compact ? 16 : 20}
            sx={{ display: "inline-block" }}
          />
        ) : (
          windText
        )}
      </Typography>
    </Box>
  );
};
